import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { DefaultDateFormatter, intl, useTrackElementResize } from '../../lib';
import { isEmptyArray } from '../../util';
import { D3Tooltip } from './D3Tooltip';
import * as d3Import from 'd3';
import { D3Axis } from './D3Axis';
import type { ClassNameProp, D3Props } from '../common-types';

/**
 * Type for the chart data.
 */
interface TimerangeDataType<T> {
  /**
   * The data that the bars are shown for.
   */
  data: T[];
  /**
   * The date.
   */
  date: string;
}

/**
 * Common props for both the {@link TimerangeBarChart} and the internal {@link TimerangeBarChartSVG}.
 */
interface TimerangeBarChartCommonProps<T> extends D3Props {
  /**
   * Timerange data to display the chart for.
   */
  data: TimerangeDataType<T>[];
  /**
   * Determines if the yAxis should be shown or not.
   */
  showYAxis?: boolean;

  /**
   * The maximum number of ticks displayed in the y axis.
   * (These are the ticks between 0 and the max value of the yAxis, which means in total a maximum number of 4 ticks is displayed by default)
   */
  yAxisMaxTicks?: number;

  /**
   * Determines if the year lines should be shown or not.
   */
  showYears?: boolean;

  /**
   * Determines if dates inside the data should be combined into a single record or not.
   */
  combineDates?: boolean;

  /**
   * The maximum number of bars that should be displayed when dates are being combined.
   */
  maxBarNumber?: number;

  /**
   * The maxWidth of the bars.
   */
  maxBarWidth?: number;

  /**
   * The text to display inside a bar's tooltip to describe the number of records.
   */
  tooltipText?: string;

  /**
   * The text that is displayed when the rendered data is empty.
   */
  emptyText?: string;
}

/**
 * Data type returned by the {@link mapTimerangeData} method which is used to map the data
 * for the {@link TimerangeBarChart}.
 */
type MappedDataType<T> = TimerangeDataType<T> & {
  /**
   * Year of the data.
   */
  year: number;
};

/**
 * Maps the given data into a more usable format for the {@link TimerangeBarChart} component.
 * If <code>combineDates</code> is true, then the dates will be combined to have only a maximum of <code>maxBars</code>.
 *
 * @param data the data to map
 * @param combineDates if dates should be combined
 * @param maxBars maximum number of bars to combine to
 * @return the mapped data
 */
function mapTimerangeData<T>(
  data: TimerangeDataType<T>[],
  combineDates: boolean,
  maxBars: number
): MappedDataType<T>[] {
  // simply map the data to include a 'year' property when not combining the data.
  if (data.length <= maxBars || !combineDates) {
    return data.map(d => ({
      ...d,
      year: new Date(d.date).getFullYear()
    }));
  }

  // combine the data to a max length of maxBars
  const numberToCombine = Math.ceil(data.length / maxBars);
  let currentCombine = 1;
  const combined: MappedDataType<T>[] = [];

  // function for creating a new MappedDataType object
  const newMappedDataType = (): MappedDataType<T> => {
    return {
      date: '',
      data: [],
      year: 0
    };
  };
  let currentData = newMappedDataType();

  // combine records in the data so that only maxBars combined ones remain
  for (const i in data) {
    if (currentCombine == 1) {
      // new record
      currentData.date = DefaultDateFormatter.formatDate(data[i].date);
      currentData.data = [];
      for (const j in data[i].data) {
        currentData.data.push(data[i].data[j]);
      }
      currentData.year = new Date(data[i].date).getFullYear();
      combined.push(currentData);
      currentCombine++;
    } else if (currentCombine < numberToCombine) {
      // data inside a range
      currentData.data.push(...data[i].data);
      currentCombine++;
    } else {
      // last date in a combined range
      currentData.data.push(...data[i].data);
      const toDate = DefaultDateFormatter.formatDate(data[i].date);
      if (currentData.date !== toDate) {
        currentData.date +=
          ' - ' + DefaultDateFormatter.formatDate(data[i].date);
      }
      currentCombine = 1;
      currentData = newMappedDataType();
    }
  }

  // combine records that have the same date
  const returnData: MappedDataType<T>[] = [];
  for (const i in combined) {
    if (returnData.length === 0) {
      returnData.push(combined[i]);
    } else if (returnData[returnData.length - 1].date === combined[i].date) {
      const r = returnData[returnData.length - 1];
      r.data.push(...combined[i].data);
    } else {
      returnData.push(combined[i]);
    }
  }

  // returned the mapped and combined data
  return returnData;
}

/**
 * Props for a {@link TimerangeBarChart}.
 */
export type TimerangeBarChartProps<T> = TimerangeBarChartCommonProps<T> &
  ClassNameProp;

/**
 * Component for displaying the document timerange bar chart.
 * The chart will be sized automatically depending on the available size it has in the parent component.
 * If the data is empty, then a simple div containing the emptyText will be displayed.
 *
 * @param properties a {@link TimerangeBarChartProps} object
 * @return the created {@link JSX.Element}
 * @constructor
 */
export function TimerangeBarChart<T>({
  className = '',
  data,
  d3,
  maxBarNumber,
  maxBarWidth,
  combineDates,
  showYAxis,
  showYears,
  yAxisMaxTicks,
  tooltipText,
  emptyText,
  ...props
}: Readonly<TimerangeBarChartProps<T>>): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  // determine if data is empty
  const dataEmpty = isEmptyArray(data);
  // build the className of the div container
  const containerClasses = ['ehs-graph-timerange'];
  if (dataEmpty) {
    containerClasses.push('no-data');
  }
  if (className) {
    containerClasses.push(className);
  }
  const containerClass = containerClasses.join(' ').trim();

  // handle div container sizing to pass the correct width and height to the chart
  const [divContainerSize, setDivContainerSize] = useState({
    width: 10000,
    height: 10000
  });

  // Sets the divContainerSize depending on the offsetWidth and offsetHeight of the containerRef
  const setSize = (): void => {
    if (containerRef.current) {
      setDivContainerSize({
        width: containerRef.current.offsetWidth,
        height: containerRef.current.offsetHeight
      });
    }
  };

  // set svg size depending on initial component mount
  useLayoutEffect(setSize, []);

  // handle window resize
  useTrackElementResize(containerRef, true, setSize);

  return (
    <div {...props} ref={containerRef} className={containerClass}>
      {dataEmpty ? (
        emptyText ?? intl('common.noData')
      ) : (
        <TimerangeBarChartSVG<T>
          containerSize={divContainerSize}
          d3={d3}
          data={data}
          maxBarNumber={maxBarNumber}
          maxBarWidth={maxBarWidth}
          combineDates={combineDates}
          showYAxis={showYAxis}
          showYears={showYears}
          yAxisMaxTicks={yAxisMaxTicks}
          tooltipText={tooltipText}
          emptyText={emptyText}
        />
      )}
    </div>
  );
}

/**
 * Props for the {@link TimerangeBarChartSVG} element.
 */
type TimeRangeBarChartSVGProps<T> = TimerangeBarChartCommonProps<T> & {
  /**
   * Size of the svg element.
   */
  containerSize: {
    width: number;
    height: number;
  };
};

/**
 * {@link TimerangeBarChart} SVG part if the data is not empty.
 *
 * @param props a {@link TimeRangeBarChartSVGProps} object
 * @return the created {@link JSX.Element}
 * @constructor
 */
function TimerangeBarChartSVG<T>({
  data,
  showYAxis = true,
  yAxisMaxTicks = 2,
  showYears = true,
  combineDates = true,
  maxBarNumber = 10,
  maxBarWidth = 50,
  tooltipText = intl(
    'patientsummaryportlet.view.filter.timelineviewmodel.provideddocuments'
  ),
  containerSize,
  d3
}: Readonly<TimeRangeBarChartSVGProps<T>>): JSX.Element {
  // use the correct d3 reference
  d3 = d3 ?? d3Import;
  if (!d3.scaleBand || !d3.max || !d3.scaleLinear || !d3.axisLeft) {
    throw new Error(
      "Some required d3 dependencies are missing. Make sure that at least 'd3-scale', 'd3-axis' and 'd3-array' are on the page and accessible by the TimerangeBarChart"
    );
  }

  // map the data into a more usable format
  const mappedData = useMemo(
    () => mapTimerangeData(data, combineDates, maxBarNumber),
    [data, combineDates, maxBarNumber]
  );

  const margin = {
    top: showYears ? 30.5 : 10.5,
    right: 10.5,
    bottom: 10.5,
    left: showYAxis ? 40.5 : showYears ? 20.5 : 10.5
  };

  let width = containerSize.width - margin.left - margin.right;
  let height = containerSize.height - margin.top - margin.bottom;
  // ensure that width and height are 0 or more
  if (width < 0) {
    width = 0;
  }
  if (height < 0) {
    height = 0;
  }
  const transform = `translate(${margin.left}, ${margin.top})`;

  // x scale
  const xScale = d3
    .scaleBand()
    .domain(mappedData.map(d => d.date))
    .range([0, width])
    .padding(0.2);

  // y scale
  let yMax = d3.max(mappedData, (d: MappedDataType<T>) => d.data.length) ?? 0;
  if (yMax && yMax !== 1 && yMax % 2 != 0) {
    yMax += 1;
  }

  // adjust left margin for big numbers
  if (showYAxis && String(yMax).length > 3) {
    const additionalMargin = (String(yMax).length - 3) * 10;
    margin.left = 40.5 + additionalMargin;
    width = width - margin.left - margin.right;
  }

  const yScale = d3.scaleLinear().domain([0, yMax]).range([height, 0]);

  // y axis
  let yAxis = d3.axisLeft(yScale);
  if (yMax <= yAxisMaxTicks) {
    yAxis = yAxis.ticks(yMax);
  } else {
    yAxis.ticks(yAxisMaxTicks);
  }

  // tooltip state
  const initialTooltipState = {
    data: {
      key: '',
      value: ''
    },
    visible: false,
    position: {
      x: 0,
      y: 0
    },
    constrainX: width + margin.right
  };
  const [tooltipState, setTooltipState] = useState(initialTooltipState);

  // handler for showing the tooltip for a hovered bar
  const showTooltip = (event: React.MouseEvent): void => {
    const target = event.target as typeof event.target & {
      getAttribute: (attr: string) => string;
    };

    // get the data key that is going to be displayed in the tooltip
    let dataKey: string = target.getAttribute('data-key');
    // format it if it's a valid Date value
    if (!isNaN(new Date(dataKey).valueOf())) {
      dataKey = DefaultDateFormatter.formatDate(dataKey);
    }

    setTooltipState({
      visible: true,
      data: {
        key: dataKey,
        value: target.getAttribute('data-value')
      },
      position: {
        x:
          Number(target.getAttribute('x')) +
          Number(target.getAttribute('width')) / 2,
        y: Number(target.getAttribute('y'))
      },
      constrainX: width + margin.right
    });
  };

  // handler for hiding the tooltip for a hovered bar
  const hideTooltip = (): void => {
    setTooltipState(initialTooltipState);
  };

  // determine if a year should be drawn or not
  const drawnYears: number[] = [];
  const determineDrawYear = (d: MappedDataType<T>): boolean => {
    if (!drawnYears.includes(d.year)) {
      drawnYears.push(d.year);
      return true;
    }

    return false;
  };

  let groupCounter = 0;
  return (
    <svg
      width={width + margin.left + margin.right}
      height={height + margin.top + margin.bottom}
      className="graph-timerange-svg"
    >
      <g transform={transform}>
        {showYAxis && <D3Axis h={height} axis={yAxis} axisType="y" d3={d3} />}
        {mappedData.map(d => (
          <g key={`group-${groupCounter++}`}>
            <rect
              x={xScale(d.date)}
              y={yScale(d.data.length)}
              height={height - (yScale(d.data.length) ?? 0)}
              width={
                xScale.bandwidth() <= maxBarWidth
                  ? xScale.bandwidth()
                  : maxBarWidth
              }
              opacity={0.9}
              className="timerange-rect"
              data-value={d.data.length}
              data-key={d.date}
              onMouseOver={showTooltip}
              onMouseOut={hideTooltip}
            />
            {showYears && determineDrawYear(d) && (
              <>
                <polyline
                  points={`${(xScale(d.date) ?? 0) - 25},${-25}, ${xScale(
                    d.date
                  )},0, ${xScale(d.date)},${height}`}
                  className="date-line"
                />
                <text
                  transform={`translate(${
                    (xScale(d.date) ?? 0) - 20
                  }, -25), rotate(45)`}
                  className="date-text"
                >
                  {d.year}
                </text>
              </>
            )}
          </g>
        ))}
        <D3Tooltip
          {...tooltipState}
          className="ehs-graph-timerange-tooltip"
          width={
            // determine width depending on the data
            // TODO: improve this automatic sizing
            30 +
            Math.max(
              tooltipState.data.key.length,
              `${tooltipText}: ${tooltipState.data.value}`.length
            ) *
              7
          }
          height={55}
        >
          <tspan x="0" textAnchor="start">
            {tooltipState.data?.key}
          </tspan>
          <tspan x="0" dy="20" textAnchor="start">
            {tooltipText}:
            <tspan className="tooltip-documents">
              {' '}
              {tooltipState.data?.value}
            </tspan>
          </tspan>
        </D3Tooltip>
      </g>
    </svg>
  );
}
